/**
 * PATH: apps/ai/src/pages/openclaw-concepts/PasswordGate.tsx
 * PURPOSE: Password protection gate for concept pages
 */

import { useState } from 'react'
import { Brain, Eye, EyeOff, AlertTriangle } from 'lucide-react'

const CONCEPT_PASSWORD = 'quotech2026'
export const STORAGE_KEY = 'quotech-concepts-auth'

export function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === CONCEPT_PASSWORD) { sessionStorage.setItem(STORAGE_KEY, 'true'); onUnlock() }
    else { setError(true); setTimeout(() => setError(false), 2000) }
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>
      <div className="relative w-full max-w-md z-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-cyan-500 via-blue-500 to-purple-600 mb-6 shadow-2xl shadow-cyan-500/20"><Brain className="w-10 h-10 text-white" /></div>
          <h1 className="text-3xl font-bold text-white mb-2">The Future of Quotech AI</h1>
          <p className="text-slate-400">24/7 Proactive Intelligence Research</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-slate-900/80 backdrop-blur-xl rounded-3xl border border-slate-700/50 p-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-3">Access Code</label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className={`w-full px-5 py-4 rounded-xl bg-slate-800/50 border ${error ? 'border-red-500 shake' : 'border-slate-600/50'} text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all text-lg`} placeholder="Enter password" autoFocus />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>
            </div>
            {error && <p className="mt-3 text-sm text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Invalid password</p>}
          </div>
          <button type="submit" className="w-full py-4 px-6 rounded-xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 text-white font-semibold text-lg hover:opacity-90 transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:ring-offset-2 focus:ring-offset-slate-900 shadow-lg shadow-cyan-500/25">Unlock Research</button>
        </form>
        <p className="mt-8 text-center text-xs text-slate-600">Quotech AI Hub - Confidential Research Document</p>
      </div>
    </div>
  )
}
